import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tooltip } from 'antd';
import { Pencil, Trash2, Building2 } from 'lucide-react';

import TableFilters from './TableFilters';
import { TagsDisplay } from '../../tags/elements';
import { IconAction, IconActions, StatusTag, EmptyState } from '../../common';
import { useClientTags } from './clientTable.controller';

/** Short date with the full timestamp in a tooltip. */
const DateCell = ({ date }) =>
    date ? (
        <Tooltip title={new Date(date).toLocaleString('es-ES')}>
            <span className="text-sm text-surface-600 tabular-nums">{new Date(date).toLocaleDateString('es-ES')}</span>
        </Tooltip>
    ) : (
        <span className="text-surface-300">--</span>
    );

export default function ClientTable({ rows, setRows, clients, loading, showDrawer, updateClientById }) {
    const navigate = useNavigate();
    const { availableTags, hasTag } = useClientTags(clients);

    const ClientTableColumns = [
        {
            title: 'Cliente',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            width: 240,
            render: (text, record) => (
                <div className="min-w-0">
                    <button
                        type="button"
                        onClick={() => navigate(`/dashboard/clients/client?id=${record._id}`)}
                        className="block max-w-full truncate text-left text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors">
                        {text}
                    </button>
                    <span className="block text-xs text-surface-500 tabular-nums">
                        <span className="font-medium">RIF:</span> {record.rif}
                    </span>
                </div>
            ),
            sorter: (a, b) => a.name.localeCompare(b.name)
        },
        {
            title: 'Estado',
            dataIndex: 'active',
            key: 'active',
            width: 120,
            render: (active) => (
                <StatusTag tone={active === 'activo' ? 'success' : 'danger'} dot>
                    {active === 'activo' ? 'Activo' : 'Inactivo'}
                </StatusTag>
            ),
            filters: [
                { text: 'Activo', value: 'activo' },
                { text: 'Inactivo', value: 'inactivo' }
            ],
            onFilter: (value, record) => record.active === value
        },
        {
            title: 'Canal',
            dataIndex: 'channel',
            key: 'channel',
            width: 130,
            render: (text) => <StatusTag tone={text === 'moderno' ? 'info' : 'warning'}>{text === 'moderno' ? 'Moderno' : 'Tradicional'}</StatusTag>,
            filters: [
                { text: 'Moderno', value: 'moderno' },
                { text: 'Tradicional', value: 'tradicional' }
            ],
            onFilter: (value, record) => record.channel === value
        },
        {
            title: 'Gerente',
            dataIndex: 'gerente',
            key: 'gerente',
            width: 170,
            render: (text) => <span className="text-sm text-surface-700">{text || '--'}</span>
        },
        {
            title: 'Asesores',
            dataIndex: 'asesores',
            key: 'asesores',
            width: 220,
            render: (asesores) => (
                <div className="flex flex-wrap gap-1">
                    {asesores && asesores.length > 0 ? (
                        asesores.slice(0, 2).map((asesor, idx) => (
                            <StatusTag key={idx} tone="neutral">
                                {asesor}
                            </StatusTag>
                        ))
                    ) : (
                        <span className="text-xs text-surface-400">Sin asignar</span>
                    )}
                    {asesores && asesores.length > 2 && (
                        <Tooltip title={asesores.slice(2).join(', ')}>
                            <StatusTag tone="brand" className="cursor-pointer">
                                +{asesores.length - 2}
                            </StatusTag>
                        </Tooltip>
                    )}
                </div>
            )
        },
        {
            title: 'Creado',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 120,
            render: (date) => <DateCell date={date} />,
            sorter: (a, b) => (a.createdAt && b.createdAt ? new Date(a.createdAt) - new Date(b.createdAt) : 0)
        },
        {
            title: 'Actualizado',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 120,
            render: (date) => <DateCell date={date} />,
            sorter: (a, b) => (a.updatedAt && b.updatedAt ? new Date(a.updatedAt) - new Date(b.updatedAt) : 0)
        },
        {
            title: 'Etiquetas',
            dataIndex: '_id',
            key: 'tags',
            width: 200,
            render: (id) => <TagsDisplay modelType="Client" modelId={id} editable={true} />,
            filters: availableTags.map((tag) => ({
                text: tag.name,
                value: tag._id
            })),
            filterMultiple: true,
            onFilter: (value, record) => hasTag(record._id, value)
        },
        {
            title: 'Acciones',
            dataIndex: '_id',
            key: '_id',
            fixed: 'right',
            align: 'right',
            width: 96,
            render: (text) => (
                <IconActions>
                    <IconAction icon={<Pencil />} label="Editar" onClick={async () => await showDrawer(text)} />
                    <IconAction
                        icon={<Trash2 />}
                        label="Eliminar"
                        danger
                        onClick={async () => await updateClientById(text)}
                        confirm={{
                            title: 'Borrar Cliente',
                            description: '¿Estás seguro que deseas eliminar este cliente?',
                            okText: 'Sí',
                            cancelText: 'No'
                        }}
                    />
                </IconActions>
            )
        }
    ];

    return (
        <>
            <TableFilters setRows={setRows} clientsData={clients} />
            <section className="shelf-card overflow-hidden">
                <Table
                    loading={loading}
                    columns={ClientTableColumns}
                    dataSource={rows}
                    size="middle"
                    pagination={{
                        pageSize: 15,
                        showSizeChanger: true,
                        showTotal: (total) => (
                            <span className="text-surface-500">
                                Total <strong className="tabular-nums">{total}</strong> clientes
                            </span>
                        )
                    }}
                    scroll={{ x: 1200 }}
                    locale={{
                        emptyText: <EmptyState icon={<Building2 />} title="No hay clientes" description="Prueba con otros filtros o crea un cliente nuevo." />
                    }}
                />
            </section>
        </>
    );
}
